import { useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

const TYPES = ['FORM', 'WORKFLOW', 'DASHBOARD', 'PORTAL'];

/**
 * GR L28.02 — New Application (`/lcnc/apps/new`). The builder entry point that
 * creates a fresh `lcnc_applications` record. Enforces §M field rules: name
 * required 3–120 chars, an application type chosen from the allowed set. On
 * success surfaces the new application code.
 */
export function GrAppNewPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [applicationType, setApplicationType] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function validate(): string | null {
    if (name.trim().length < 3 || name.trim().length > 120) return tr('gr28.appNew.validName');
    if (!applicationType) return tr('gr28.appNew.validType');
    return null;
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const v = validate();
    if (v) { setValidation(v); return; }
    setValidation(null);
    setSubmitting(true);
    try {
      const r = await apiRequest<{ data: { applicationCode: string } }>('/v1/lcnc/applications', {
        token, method: 'POST',
        body: { name: name.trim(), applicationType, category: category.trim(), description: description.trim() },
      });
      setCreated(r.data.applicationCode);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main aria-label="lcnc-app-new">
      <h1>{tr('gr28.appNew.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {created && <p aria-label="created">{tr('gr28.appNew.created', { code: created })}</p>}
      {validation && <p aria-label="validation" role="status">{validation}</p>}
      <form onSubmit={submit}>
        <label>
          {tr('gr28.appNew.labelName')}
          <input aria-label="input-name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          {tr('gr28.appNew.labelType')}
          <select aria-label="input-type" value={applicationType} onChange={(e) => setApplicationType(e.target.value)}>
            <option value="">{tr('gr28.appNew.select')}</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          {tr('gr28.appNew.labelCategory')}
          <input aria-label="input-category" value={category} onChange={(e) => setCategory(e.target.value)} />
        </label>
        <label>
          {tr('gr28.appNew.labelDescription')}
          <textarea aria-label="input-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <button type="submit" aria-label="submit-app" disabled={submitting}>
          {submitting ? tr('gr28.appNew.submitting') : tr('gr28.appNew.submit')}
        </button>
      </form>
    </main>
  );
}
